import { AnyAction } from 'redux';
import { Injectable } from '@angular/core';
import { TARIF_CHECKED } from '@ps20-frontend/shop-integration-redux';
import { Leistung, Produktlinie, ZusatzoptionItem } from '@ps20-frontend/frontend-models';
import { ProfiSport } from '@ps20/bff-models';
import { ZusatzoptionenCheck } from './action-types/zusatzoptionenCheck';
import { IAppState } from './appState';
import { AntragsummerGenerated } from './action-types/antragsummerGenerated';
import { Berufsrisikogruppe } from './model/unf.enums';

interface ZusatzoptionInput {
  value: string;
  id: string;
}
interface LeistungBeitragPayload {
  id: Leistung['id'];
  produktName: Produktlinie['produktName'];
  value: Leistung['eigenschaft'];
}

export interface UnfAction<T = undefined> extends AnyAction {
  payload?: T;
}

@Injectable()
export class UnfActions {
  static readonly ABSPRUNG_INS_DASHBOARD = 'ABSPRUNG_INS_DASHBOARD';
  static readonly VORSCHLAGDATEN_CHANGED = 'VORSCHLAGDATEN_CHANGED';
  static readonly BERUFSRISIKOGRUPPE_CHANGED = 'BERUFSRISIKOGRUPPE_CHANGED';
  static readonly PREISE_REQUESTED = 'PREISE_REQUESTED';
  static readonly ANTRAGSNUMMER_GENERATED = 'ANTRAGSNUMMER_GENERATED';
  static readonly ZURUECK_ZUM_SHOP_MODAL_OPEN = 'ZURUECK_ZUM_SHOP_MODAL_OPEN';
  static readonly ZURUECK_ZUM_SHOP_MODAL_CLOSE = 'ZURUECK_ZUM_SHOP_MODAL_CLOSE';
  static readonly ZUSATZOPTION_CHECKED = 'ZUSATZOPTION_CHECKED';
  static readonly ZUSATZOPTION_UNCHECKED = 'ZUSATZOPTION_UNCHECKED';
  static readonly ZUSATZOPTIONINPUT_CHANGED = 'ZUSATZOPTIONINPUT_CHANGED';
  static readonly LEISTUNG_EIGENSCHAFT_CHANGED = 'LEISTUNG_EIGENSCHAFT_CHANGED';
  static readonly SELECTED_INVSUMME_CHANGED = 'SELECTED_INVSUMME_CHANGED';
  static readonly GOTOKUS = 'GOTOKUS';
  static readonly GOTOKUS_SUCCESSFUL = 'GOTOKUS_SUCCESSFUL';
  static readonly GOTOKUS_FAILED = 'GOTOKUS_FAILED';
  static readonly QUOTEDB_REQUESTED = 'QUOTEDB_REQUESTED';
  static readonly SPECIAL_SPORT_RISK_DATA_CHANGED = 'SPECIAL_SPORT_RISK_DATA_CHANGED';
  static readonly SPECIAL_PROFI_SPORT_ZUSATZOPTION = 'SPECIAL_PROFI_SPORT_ZUSATZOPTION';
  static readonly HANDLE_UNF_NAVIGATION_TO_ANGEBOT = 'HANDLE_UNF_NAVIGATION_TO_ANGEBOT';

  absprungInsDashboard(): UnfAction<void> {
    return { type: UnfActions.ABSPRUNG_INS_DASHBOARD };
  }

  quoteDBRequested(): UnfAction<void> {
    return { type: UnfActions.QUOTEDB_REQUESTED };
  }

  vorschlagdatenChanged(angebotsId: string): UnfAction<string> {
    return { type: UnfActions.VORSCHLAGDATEN_CHANGED, payload: angebotsId };
  }

  goToKusAction(): UnfAction<void> {
    return { type: UnfActions.GOTOKUS };
  }

  berufsrisikogruppeChanged(berufsrisikogruppe: Berufsrisikogruppe): UnfAction<Berufsrisikogruppe> {
    return { type: UnfActions.BERUFSRISIKOGRUPPE_CHANGED, payload: berufsrisikogruppe };
  }

  antragsnummerGenerated(
    antragsnummer: IAppState['antragsnummer'],
    angebotsId: IAppState['angebotsId'],
  ): UnfAction<AntragsummerGenerated> {
    return {
      type: UnfActions.ANTRAGSNUMMER_GENERATED,
      payload: { antragsnummer, angebotsId },
    };
  }

  zurueckZumShopModalOpen(): UnfAction<void> {
    return { type: UnfActions.ZURUECK_ZUM_SHOP_MODAL_OPEN };
  }

  zurueckZumShopModalClose(): UnfAction<void> {
    return { type: UnfActions.ZURUECK_ZUM_SHOP_MODAL_CLOSE };
  }

  preiseRequested(): UnfAction<void> {
    return { type: UnfActions.PREISE_REQUESTED };
  }

  tarifChecked(index: number): UnfAction<{ index: number }> {
    return { type: TARIF_CHECKED, payload: { index } };
  }

  zusatzoptionChecked(name: ZusatzoptionItem['name']): UnfAction<ZusatzoptionenCheck> {
    return { type: UnfActions.ZUSATZOPTION_CHECKED, payload: { name } };
  }

  zusatzoptionUnchecked(name: ZusatzoptionItem['name']): UnfAction<ZusatzoptionenCheck> {
    return { type: UnfActions.ZUSATZOPTION_UNCHECKED, payload: { name } };
  }

  specialProfiSportSelected(selected: boolean): UnfAction<{ selected: boolean}> {
    return { type: UnfActions.SPECIAL_PROFI_SPORT_ZUSATZOPTION, payload: { selected } };
  }

  leistungEigenschaftChanged(
    id: Leistung['id'],
    produktName: Produktlinie['produktName'],
    value: Leistung['eigenschaft'],
  ): UnfAction<LeistungBeitragPayload> {
    return {
      type: UnfActions.LEISTUNG_EIGENSCHAFT_CHANGED,
      payload: { id, value, produktName },
    };
  }
  zusatzoptionInputChanged(
    value: ZusatzoptionItem['eigenschaft'],
    id: ZusatzoptionItem['id'],
  ): UnfAction<ZusatzoptionInput> {
    return {
      type: UnfActions.ZUSATZOPTIONINPUT_CHANGED,
      payload: { value, id },
    };
  }

  selectedInvsummeChanged(
    invSumme: Leistung['eigenschaft'],
  ): UnfAction<{ invSumme: Leistung['eigenschaft'] }> {
    return {
      type: UnfActions.SELECTED_INVSUMME_CHANGED,
      payload: { invSumme },
    };
  }

  specialSportRiskDataChanged(data: ProfiSport): UnfAction<ProfiSport> {
    return {
      type: UnfActions.SPECIAL_SPORT_RISK_DATA_CHANGED,
      payload: data,
    };
  }

  handleUnfNavigationToAngebot(): UnfAction {
    return { type: UnfActions.HANDLE_UNF_NAVIGATION_TO_ANGEBOT };
  }
}

const _globalActions = new UnfActions();
export type AntragsnummerGeneratedAction = ReturnType<typeof _globalActions.antragsnummerGenerated>;
export type ZusatzoptionInputChangedAction = ReturnType<
  typeof _globalActions.zusatzoptionInputChanged
>;
export type LeistungEigenschaftChangedAction = ReturnType<
  typeof _globalActions.leistungEigenschaftChanged
>;
export type TarifCheckedAction = ReturnType<typeof _globalActions.tarifChecked>;
export type SelectedInvsummeChanged = ReturnType<typeof _globalActions.selectedInvsummeChanged>;
export type GoToKusAction = ReturnType<typeof _globalActions.goToKusAction>;
export type SpecialSportRiskDataChangedAction = ReturnType<
  typeof _globalActions.specialSportRiskDataChanged
>;
export type SpecialProfiSportSelectedAction = ReturnType<
  typeof _globalActions.specialProfiSportSelected
>;
